import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Application } from '@feathersjs/feathers';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { AutoScouter } from '../../app/feathers/auto-scouter/AutoScouter';
import { AutoScouterDetailContent } from './index';
import { LoadingSpinner } from '../../components';
import { Link } from 'react-router-dom';
import { resolveAutoScouter } from './resolveAutoScouter';

interface AutoScouterScoutingSessionProps {
  electionKey: string;
  districtId: string;
  showToolbar?: boolean;
  createEntitiesTaskId?: string;
  onAutoScouterChange?: (autoScouter: AutoScouter) => void;
  onScoutingConfirmed?: (autoScouter: AutoScouter) => void;
}

const AutoScouterScoutingSession = ({
  electionKey,
  districtId,
  showToolbar = false,
  createEntitiesTaskId,
  onAutoScouterChange,
  onScoutingConfirmed
}: AutoScouterScoutingSessionProps) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);
  const [autoScouterId, setAutoScouterId] = useState<string | null>(null);
  const [stateMatchName, setStateMatchName] = useState<string | null>(null);

  useEffect(() => {
    if (!feathers || !electionKey || !districtId) return;

    let active = true;
    const initializeAutoScouter = async () => {
      setLoading(true);
      setError(null);
      try {
        // First, resolve election to figure out the state code and state route.
        const election = await feathers.getService('elections').get(electionKey);
        const stateCode: string = election.stateCode;

        if (!stateCode) {
          throw new Error('Election is missing stateCode; cannot verify scouting configuration.');
        }

        const stateDistrict = await feathers.getService('districts').get(election.state);
        setStateMatchName(stateDistrict.matchName);

        // Load state-level configs needed for auto-scouter
        const [stateConfig, stateElectionConfig] = await Promise.all([
          feathers.getService('state-configurations').get(stateCode.toLowerCase()).catch(() => null),
          feathers.getService('state-election-configurations').get(stateCode.toUpperCase()).catch(() => null),
        ]);
        if(stateConfig === null) {
          throw new Error('State configuration not found for state code: ' + stateCode);
        }
        if(stateElectionConfig === null) {
          throw new Error('State election configuration not found for state code: ' + stateCode);
        }

        // Check for EAU types (either explicit eauTypes or any typeConfigurations with isEauType)
        const typeConfigurations = stateConfig.typeConfigurations;
        const eauTypes = typeConfigurations
          .filter((tc) => tc.isEauType === true)
          .map((tc) => tc.type);

        const hasEauTypes = Array.isArray(eauTypes) && eauTypes.some(eauType => eauType !== 'state');

        // Check that a scouting search configuration exists (autoScouter.search)
        const autoScouterConfig = stateElectionConfig.autoScouter;
        const searchConfig = autoScouterConfig?.search;
        const hasSearchConfig = !!searchConfig;

        if (!hasEauTypes || !hasSearchConfig) {
          const parts: string[] = [];
          if (!hasEauTypes) {
            parts.push('no EAU types are configured for this state');
          }
          if (!hasSearchConfig) {
            parts.push('no auto-scouter search configuration is set up in the State Configuration');
          }

          const details = parts.join(' and ');
          const message = `Cannot start auto-scouter for this district because ${details}. Please update the State Configuration first.`;

          if (active) {
            setError(message);
            setAutoScouterId(null);
          }

          // Do not create a new auto-scouter in this case
          return;
        }

        const autoScouter = await resolveAutoScouter(feathers, electionKey, districtId);
        if (active) {
          setAutoScouterId(autoScouter._id);
        }
      } catch (err: any) {
        if (active) {
          setError(err?.message || 'Failed to initialize auto-scouter session.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    initializeAutoScouter();
    return () => {
      active = false;
    };
  }, [feathers, electionKey, districtId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px' }}>
        <span>Loading scouting session...</span>
        <LoadingSpinner width={64} />
      </div>
    );
  }

  if (error || !autoScouterId) {
    return (
      <div style={{ padding: '24px' }}>
        <Typography variant="body1" color="error" gutterBottom>
          {error || 'Unable to start scouting session.'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          You can edit the State Election Configuration for this state
          {stateMatchName ? (
            <>
              {' '}on the{' '}
              <Link to={`/states/${stateMatchName}/state-config`} style={{ textDecoration: 'underline' }}>
                State configuration page
              </Link>
              .
            </>
          ) : (
            '.'
          )}
        </Typography>
      </div>
    );
  }

  return (
    <AutoScouterDetailContent
      electionKey={electionKey}
      autoScouterId={autoScouterId}
      showToolbar={showToolbar}
      createEntitiesTaskId={createEntitiesTaskId}
      onAutoScouterChange={onAutoScouterChange}
      onScoutingConfirmed={onScoutingConfirmed}
    />
  );
};

export default AutoScouterScoutingSession;
